import { useState } from 'react';
import { pb } from '@/core/api';
import { useAuthStore } from '@/stores/auth';
import { useTransitionOverlay } from '@/stores/transition-overlay';
import { blobToFile } from '@/components/upload/crop-canvas';

type PhotoUpload = {
  preview: string | null;
  hasFile: boolean;
  uploading: boolean;
  cropSrc: string | null;
  pick: (f: globalThis.File) => void;
  cancelCrop: () => void;
  commitCrop: (blob: globalThis.Blob) => void;
  upload: (igUsername: string) => Promise<void>;
};

function uploadFile(
  file: globalThis.File | null,
  igUsername: string
): Promise<unknown> {
  const form = new globalThis.FormData();
  if (file) form.append('photo', file);
  if (igUsername) form.append('ig_username', igUsername);
  return pb.send('/api/team/me/verification-photo', {
    method: 'POST',
    body: form,
  });
}

type FileState = {
  preview: string | null;
  file: globalThis.File | null;
  cropSrc: string | null;
};

const EMPTY: FileState = { preview: null, file: null, cropSrc: null };

function makeOps(
  set: (s: FileState) => void
): Pick<PhotoUpload, 'pick' | 'cancelCrop' | 'commitCrop'> {
  return {
    pick: (f) =>
      set({ preview: null, file: null, cropSrc: URL.createObjectURL(f) }),
    cancelCrop: () => set(EMPTY),
    commitCrop: (b) =>
      set({
        preview: URL.createObjectURL(b),
        file: blobToFile(b, 'verification.jpg'),
        cropSrc: null,
      }),
  };
}

type UploadDeps = {
  file: globalThis.File | null;
  setUploading: (b: boolean) => void;
  refreshAuth: () => Promise<void>;
  onDone: () => void;
};

async function runUpload(d: UploadDeps, igUsername: string): Promise<void> {
  d.setUploading(true);
  try {
    await uploadFile(d.file, igUsername);
    useTransitionOverlay.getState().show();
    await d.refreshAuth();
    d.onDone();
  } finally {
    d.setUploading(false);
  }
}

export function usePhotoUpload(onDone: () => void): PhotoUpload {
  const [s, setS] = useState<FileState>(EMPTY);
  const [uploading, setUploading] = useState(false);
  const refreshAuth = useAuthStore((st) => st.refreshAuth);
  const ops = makeOps(setS);
  const upload = (igUsername: string): Promise<void> =>
    runUpload({ file: s.file, setUploading, refreshAuth, onDone }, igUsername);
  return {
    preview: s.preview,
    hasFile: !!s.file,
    uploading,
    cropSrc: s.cropSrc,
    ...ops,
    upload,
  };
}
